import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

import { SurveyQuestion } from "../../../components/survey/SurveyQuestion";
import { ZeosSpinner } from "../../../components/Shared/ZeosSpinner";
import {
  exploreSurveyConfigBySlug,
  exploreSurveyGoalsPath,
  IExploreSurveyDomainConfig,
} from "../../../configs/ExploreSurveyConfig";
import { FeatureFlags } from "../../../configs/FeatureFlags";
import { AuthContext } from "../../../context/AuthContext";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { createLogger } from "../../../services/Logger";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import {
  buildSurveyUpdatePayload,
  hydrateSurveyAnswers,
} from "../../../services/survey/SurveyAnswerHelpers";
import {
  isQuestionType,
  isQuestionVisible,
  isQuestionnaireVisible,
  pruneHiddenAnswers,
} from "../../../services/survey/SurveyVisibilityHelpers";
import {
  ISurveyContract,
  ISurveyQuestionnaire,
} from "../../../services/zeos-api/contracts/Survey";
import {
  isComprehensiveCompletion,
  QuestionTypeEnum,
  SurveyCompletionMode,
} from "../../../services/zeos-api/enums/Survey";
import { DashboardFooter } from "../layout/DashboardFooter";
import { DashboardHeader } from "../layout/DashboardHeader";
import {
  ExploreSurveyChrome,
  ExploreSurveyResourcesSidebar,
} from "./ExploreSurveyChrome";

interface IProps {
  domainConfig?: IExploreSurveyDomainConfig;
}

export const ExploreSurveyShell: React.FC<IProps> = (props) => {
  const params = useParams<{ domain?: string }>();
  const config =
    props.domainConfig ?? exploreSurveyConfigBySlug(params.domain);
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const log = useMemo(
    () => createLogger(`ExploreSurvey:${config?.slug ?? "unknown"}`),
    [config?.slug]
  );
  const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";

  const [template, setTemplate] = useState<ISurveyContract | null>(null);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [showExistingSummary, setShowExistingSummary] = useState(false);

  const contentSteps = useMemo(() => {
    if (!template) {
      return [] as ISurveyQuestionnaire[];
    }
    return template.questionnaires
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((qn) =>
        // Keep empty "Summary" questionnaires as the final shell step instead.
        qn.questions?.length > 0 ||
        (qn.displayName || "").toLowerCase().includes("summary")
      );
  }, [template]);

  const steps = useMemo(() => {
    // Drop questionnaires hidden by visibility rules (e.g. PowerGen Site).
    const withQuestions = contentSteps.filter(
      (qn) =>
        (qn.questions?.length ?? 0) > 0 &&
        isQuestionnaireVisible(qn.visibilityRules, answers)
    );
    return [
      ...withQuestions,
      {
        questionnaireId: -1,
        displayName: "Summary",
        sortOrder: 999,
        questions: [],
      } as ISurveyQuestionnaire,
    ];
  }, [answers, contentSteps]);

  const activeStep = showExistingSummary
    ? steps[steps.length - 1]
    : steps[stepIndex];
  const isSummaryStep =
    showExistingSummary ||
    !activeStep ||
    activeStep.questionnaireId < 0 ||
    (((activeStep.displayName || "").toLowerCase().includes("summary") &&
      (activeStep.questions?.length ?? 0) === 0));

  useEffect(() => {
    setSaveMessage(null);
    setFormError(null);
  }, [stepIndex, showExistingSummary]);

  useEffect(() => {
    if (showExistingSummary) {
      return;
    }
    setStepIndex((prev) => Math.min(prev, Math.max(0, steps.length - 1)));
  }, [showExistingSummary, steps.length]);

  useEffect(() => {
    if (!template) {
      return;
    }
    setAnswers((prev) => {
      const pruned = pruneHiddenAnswers(template, prev);
      return pruned === prev ? prev : pruned;
    });
  }, [answers, template]);

  useEffect(() => {
    if (!config) {
      setLoading(false);
      setLoadError("Unknown explore survey domain.");
      return;
    }

    let cancelled = false;
    const loadAsync = async () => {
      // Domain switch reuses this component instance — clear prior survey state first.
      setLoading(true);
      setLoadError(null);
      setFormError(null);
      setSaveMessage(null);
      setTemplate(null);
      setAnswers({});
      setStepIndex(0);
      setShowExistingSummary(false);
      setSaving(false);

      try {
        const templateResponse = await api.Survey.getSurveyTemplate(
          config.templateId
        );
        if (cancelled) {
          return;
        }
        if (!templateResponse?.data) {
          setLoadError(templateResponse?.message || "Survey template not found.");
          return;
        }
        setTemplate(templateResponse.data);

        const zeosUser = await auth.zeosUserAsync();
        if (!zeosUser?.zeosUserId) {
          return;
        }
        const surveyResponse = await api.Survey.getUserSurvey(
          templateResponse.data.surveyTemplateId,
          zeosUser.zeosUserId
        );
        if (cancelled) {
          return;
        }
        const status = (surveyResponse?.zeosStatus || "").toLowerCase();
        if (
          status === "error" &&
          !surveyResponse?.data &&
          (surveyResponse?.message || "")
            .toLowerCase()
            .includes("no survey instance")
        ) {
          setAnswers({});
          setShowExistingSummary(false);
          return;
        }
        const latest = surveyResponse?.data;
        if (!latest?.answers?.length) {
          setAnswers({});
          setShowExistingSummary(false);
          return;
        }
        const hydrated = hydrateSurveyAnswers(
          templateResponse.data,
          latest.answers
        );
        setAnswers(hydrated);
        // Only comprehensive completion counts as "done" on Explore.
        setShowExistingSummary(isComprehensiveCompletion(latest.completionMode));
        log.info("Explore survey prefilled", {
          domain: config.slug,
          answers: latest.answers.length,
          completionMode: latest.completionMode,
          showExistingSummary: isComprehensiveCompletion(latest.completionMode),
        });
      } catch (err) {
        if (!cancelled) {
          setLoadError("Unable to load survey.");
          log.error("Explore survey load failed", err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAsync();
    return () => {
      cancelled = true;
    };
  }, [api.Survey, auth, config?.slug, config?.templateId, log]);

  const visibleQuestions = useMemo(() => {
    if (!activeStep || !template || isSummaryStep) {
      return [];
    }
    if (!isQuestionnaireVisible(activeStep.visibilityRules, answers)) {
      return [];
    }
    return activeStep.questions
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((question) => isQuestionVisible(question, answers));
  }, [activeStep, answers, isSummaryStep, template]);

  const summarySections = useMemo(() => {
    if (!template) {
      return [];
    }
    return template.questionnaires
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((questionnaire) => {
        if (!isQuestionnaireVisible(questionnaire.visibilityRules, answers)) {
          return null;
        }
        const items = questionnaire.questions
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .filter((question) => isQuestionVisible(question, answers))
          .map((question) => {
            const value = answers[question.questionId];
            if (value == null || value === "") {
              return null;
            }
            if (
              isQuestionType(question.questionTypeId, QuestionTypeEnum.FreeText)
            ) {
              return {
                question,
                answerLabel: String(value),
              };
            }
            const selectedIds = Array.isArray(value)
              ? value.map((id) => Number(id))
              : [Number(value)];
            const labels = selectedIds
              .map((id, index) => {
                const opt = question.options.find(
                  (o) => Number(o.responseOptionId) === id
                );
                if (!opt) {
                  return null;
                }
                if (
                  isQuestionType(
                    question.questionTypeId,
                    QuestionTypeEnum.RankedChoice
                  )
                ) {
                  const rank =
                    index === 0
                      ? "Primary"
                      : index === 1
                        ? "Secondary"
                        : index === 2
                          ? "Tertiary"
                          : `Rank ${index + 1}`;
                  return `${rank}: ${opt.displayName}`;
                }
                return opt.displayName;
              })
              .filter(Boolean);
            if (labels.length === 0) {
              return null;
            }
            return {
              question,
              answerLabel: labels.join(", "),
            };
          })
          .filter(Boolean);
        if (!items.length) {
          return null;
        }
        return {
          title: questionnaire.displayName,
          items,
        };
      })
      .filter(Boolean) as Array<{
      title: string;
      items: Array<{ question: { displayName: string }; answerLabel: string }>;
    }>;
  }, [answers, template]);

  const validateCurrentStep = (): boolean => {
    if (isSummaryStep || !activeStep) {
      return true;
    }
    for (const question of visibleQuestions) {
      const value = answers[question.questionId];
      if (
        isQuestionType(question.questionTypeId, QuestionTypeEnum.FreeText)
      ) {
        continue;
      }
      const minSelect = question.minSelect ?? 1;
      if (Array.isArray(value)) {
        if (value.length < minSelect) {
          setFormError(
            `Please answer: ${question.displayName}`
          );
          return false;
        }
      } else if (value == null || value === "") {
        setFormError(`Please answer: ${question.displayName}`);
        return false;
      }
    }
    setFormError(null);
    return true;
  };

  const saveSurveyAsync = async (): Promise<boolean> => {
    if (!template || !config) {
      return false;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      const zeosUser = await auth.zeosUserAsync();
      if (!zeosUser?.zeosUserId) {
        setFormError("User not found.");
        return false;
      }
      const pruned = pruneHiddenAnswers(template, answers);
      const payload = buildSurveyUpdatePayload(
        template,
        zeosUser.zeosUserId,
        pruned,
        { completionMode: SurveyCompletionMode.Comprehensive }
      );
      const updateResponse = await api.Survey.updateSurveyInstance(payload);
      const updateStatus = (updateResponse?.zeosStatus || "").toLowerCase();
      if (updateStatus === "error" || updateResponse?.data === false) {
        log.info("Explore survey update not found, creating new instance", {
          domain: config.slug,
        });
        await api.Survey.submitSurveyInstance(payload);
      }
      setSaveMessage("Progress saved.");
      log.info("Explore survey saved", {
        domain: config.slug,
        answers: payload.answers.length,
      });
      return true;
    } catch (err) {
      log.error("Explore survey save failed", err);
      setFormError("Unable to save survey responses.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (!validateCurrentStep()) {
      return;
    }
    const saved = await saveSurveyAsync();
    if (!saved) {
      return;
    }
    if (isSummaryStep) {
      if (config) {
        navigate(exploreSurveyGoalsPath(exploreSlug, config.slug));
      }
      return;
    }
    if (stepIndex >= steps.length - 1) {
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const goBack = () => {
    if (showExistingSummary) {
      setShowExistingSummary(false);
      setStepIndex(0);
      return;
    }
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleRetakeSurvey = () => {
    setShowExistingSummary(false);
    setAnswers({});
    setStepIndex(0);
    setSaveMessage(null);
    setFormError(null);
  };

  const handleGenerateRecommendations = () => {
    if (!config) {
      return;
    }
    navigate(exploreSurveyGoalsPath(exploreSlug, config.slug));
  };

  if (!config) {
    return (
      <div className="w-screen min-h-screen bg-zeos-white-md flex flex-col">
        <DashboardHeader />
        <div className="flex-1 flex items-center justify-center p-8">
          Unknown explore survey.
        </div>
        <DashboardFooter />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-screen min-h-screen bg-zeos-white-md flex flex-col">
        <DashboardHeader />
        <div className="flex-1 flex items-center justify-center">
          <ZeosSpinner />
        </div>
        <DashboardFooter />
      </div>
    );
  }

  if (loadError || !template) {
    return (
      <div className="w-screen min-h-screen bg-zeos-white-md flex flex-col">
        <DashboardHeader />
        <div className="flex-1 flex items-center justify-center p-8 text-red-600">
          {loadError || "Survey template not found."}
        </div>
        <DashboardFooter />
      </div>
    );
  }

  return (
    <div className="w-screen min-h-screen bg-zeos-white-md flex flex-col">
      <DashboardHeader />
      <div className="flex-1 w-full">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-zeos-black">
              {config.breadcrumb}
              <div className="w-6 h-1 bg-zeos-orange-dk mt-2" />
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="border border-gray-300 rounded-md px-4 py-2 text-sm w-64 bg-white"
              />
              <FaSearch className="absolute right-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 mt-6">
            <div
              className={`col-span-12 space-y-6 ${
                FeatureFlags.enableExploreResourcesSidebar ? "lg:col-span-9" : ""
              }`}
            >
              <ExploreSurveyChrome
                activeSlug={config.slug}
                stepLabels={steps.map(
                  (step) => step.displayName || "Step"
                )}
                activeStepIndex={
                  showExistingSummary ? steps.length - 1 : stepIndex
                }
              />

              <div className="bg-white border rounded p-6 space-y-6">
                {isSummaryStep ? (
                  <>
                    <div>
                      <h2 className="text-2xl font-semibold text-zeos-black">
                        {showExistingSummary
                          ? `Your ${config.title} Survey`
                          : config.summaryTitle}
                      </h2>
                      <p className="text-sm text-gray-600 mt-2">
                        {showExistingSummary
                          ? "We already have your latest responses on file. Would you like to re-take the survey or continue with these answers?"
                          : config.summaryBody}
                      </p>
                    </div>
                    <div className="space-y-6">
                      {summarySections.map((section) => (
                        <div key={section.title} className="space-y-3">
                          <div className="text-sm font-semibold text-gray-700">
                            {section.title}
                          </div>
                          <div className="space-y-3">
                            {section.items.map((item) => (
                              <div
                                key={item.question.displayName}
                                className="border-l-4 border-zeos-orange-md pl-4 py-2"
                              >
                                <div className="text-sm font-semibold text-zeos-black">
                                  {item.question.displayName}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {item.answerLabel}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        className="px-6 py-2 rounded bg-zeos-orange-dk text-white text-sm"
                        onClick={handleGenerateRecommendations}
                      >
                        Generate Recommendations
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2 rounded border border-zeos-orange-dk text-zeos-orange-dk text-sm"
                        onClick={handleRetakeSurvey}
                      >
                        Re-take Survey
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-semibold text-zeos-black">
                      {activeStep?.displayName}
                    </h2>
                    <div className="space-y-6">
                      {visibleQuestions.map((question) => {
                        const selectHint =
                          (isQuestionType(
                            question.questionTypeId,
                            QuestionTypeEnum.MultiChoice
                          ) ||
                            isQuestionType(
                              question.questionTypeId,
                              QuestionTypeEnum.RankedChoice
                            )) &&
                          question.maxSelect != null
                            ? isQuestionType(
                                question.questionTypeId,
                                QuestionTypeEnum.RankedChoice
                              )
                              ? `Select and rank up to ${question.maxSelect}.`
                              : `Select up to ${question.maxSelect}.`
                            : null;

                        return (
                          <div
                            key={question.questionId}
                            className="border-l-4 border-zeos-orange-md pl-4 py-2"
                          >
                            <div className="text-sm font-semibold text-zeos-black">
                              {question.displayName}
                            </div>
                            {selectHint && (
                              <p className="text-xs text-gray-500 mt-1">
                                {selectHint}
                              </p>
                            )}
                            <div className="mt-3">
                              <SurveyQuestion
                                question={question}
                                isGate={false}
                                answers={answers}
                                setAnswers={setAnswers}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center gap-4 pt-2">
                      <button
                        type="button"
                        className="text-sm text-zeos-orange-dk hover:underline disabled:opacity-50"
                        onClick={() => {
                          void saveSurveyAsync();
                        }}
                        disabled={saving}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="px-6 py-2 rounded bg-zeos-orange-dk text-white text-sm disabled:opacity-50"
                        onClick={() => {
                          void goNext();
                        }}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save & Continue"}
                      </button>
                      {stepIndex > 0 && (
                        <button
                          type="button"
                          className="ml-auto px-4 py-2 rounded border border-zeos-orange-dk text-zeos-orange-dk text-sm disabled:opacity-50"
                          onClick={goBack}
                          disabled={saving}
                        >
                          Back
                        </button>
                      )}
                    </div>
                  </>
                )}

                {saveMessage && (
                  <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
                    {saveMessage}
                  </div>
                )}
                {formError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                    {formError}
                  </div>
                )}
              </div>
            </div>

            {FeatureFlags.enableExploreResourcesSidebar && (
              <ExploreSurveyResourcesSidebar />
            )}
          </div>
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
};
